import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Typography,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, Visibility as ViewIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { progressHeatColor } from './okrColors';

export default function OkrObjectivesList({
  title = 'Objectives & key results',
  description = 'Each objective holds key results with weights totaling 100%. Codes are generated as KR-XXXX-XXXX-XXXX.',
  scoreboardMode = false,
  allowManageActions = true
}) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/okr');
      setRows(r.data?.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const del = async (id) => {
    if (!window.confirm('Delete this objective and its key results?')) return;
    try {
      await api.delete(`/okr/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
        {allowManageActions && hasPermission('okr_add') && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/okr/objectives/new')}>
            New objective
          </Button>
        )}
      </Box>

      {loading ? (
        <LinearProgress />
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Objective</TableCell>
                <TableCell>Key Results category</TableCell>
                {!scoreboardMode && <TableCell>Status</TableCell>}
                <TableCell align="right">Score</TableCell>
                <TableCell align="right">Portfolio</TableCell>
                {!scoreboardMode && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={scoreboardMode ? 4 : 6}>
                    <Typography color="text.secondary">No objectives yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const score = Number(row.computed_score || 0);
                  const heat = progressHeatColor(score);
                  return (
                    <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/okr/objectives/${row.id}`)}>
                      <TableCell>
                        <Typography fontWeight={600}>{row.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.period_label || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.category?.name || '—'}</TableCell>
                      {!scoreboardMode && (
                        <TableCell>
                          <Chip size="small" label={row.status} />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={`${score.toFixed(1)}%`}
                          sx={{ bgcolor: heat.bg, color: heat.fg, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell align="right">{Number(row.weighted_contribution || 0).toFixed(1)}</TableCell>
                      {!scoreboardMode && (
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => navigate(`/okr/objectives/${row.id}`)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {allowManageActions && hasPermission('okr_modify') && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => navigate(`/okr/objectives/${row.id}/edit`)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {allowManageActions && hasPermission('okr_delete') && (
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => del(row.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      )}
    </Box>
  );
}
